/**
 * Utility functions to convert outlet data from API to user-friendly display text
 */

// Outlet Type mappings
export const outletTypeMap: Record<string, string> = {
  grocery: 'Tiệm tạp hóa',
  convenience: 'Cửa hàng tiện lợi',
  restaurant: 'Nhà hàng',
  minishop: 'Cửa hàng mini'
};

// Operating Hours mappings
export const operatingHoursMap: Record<string, string> = {
  day_only: 'Chỉ ban ngày',
  '24_7': 'Mở cửa 24/7'
};

// Store Size mappings
export const storeSizeMap: Record<string, string> = {
  large: 'Lớn',
  medium: 'Vừa',
  small: 'Nhỏ'
};

// Customer Volume mappings
export const customerVolumeMap: Record<string, string> = {
  high: 'Cao',
  medium: 'Trung bình',
  low: 'Thấp'
};

// Customer Type mappings
export const customerTypeMap: Record<string, string> = {
  family: 'Gia đình',
  youth: 'Giới trẻ'
};

/**
 * Convert outlet type to display text
 */
export function getOutletTypeText(outletType: string): string {
  return outletTypeMap[outletType] || outletType;
}

/**
 * Convert operating hours to display text
 */
export function getOperatingHoursText(operatingHours: string): string {
  return operatingHoursMap[operatingHours] || operatingHours;
}

/**
 * Convert store size to display text
 */
export function getStoreSizeText(storeSize: string): string {
  return storeSizeMap[storeSize] || storeSize;
}

/**
 * Convert customer volume to display text
 */
export function getCustomerVolumeText(customerVolume: string): string {
  return customerVolumeMap[customerVolume] || customerVolume;
}

/**
 * Convert customer type to display text
 */
export function getCustomerTypeText(customerType: string): string {
  return customerTypeMap[customerType] || customerType;
}

/**
 * Get all outlet type options for forms
 */
export function getOutletTypeOptions() {
  return Object.entries(outletTypeMap).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * Get all operating hours options for forms
 */
export function getOperatingHoursOptions() {
  return Object.entries(operatingHoursMap).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * Get all store size options for forms
 */
export function getStoreSizeOptions() {
  return Object.entries(storeSizeMap).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * Get all customer volume options for forms
 */
export function getCustomerVolumeOptions() {
  return Object.entries(customerVolumeMap).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * Get all customer type options for forms
 */
export function getCustomerTypeOptions() {
  return Object.entries(customerTypeMap).map(([value, label]) => ({
    value,
    label
  }));
}

/**
 * Get badge variant based on store size
 */
export function getStoreSizeBadgeVariant(storeSize: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (storeSize) {
    case 'large':
      return 'default';
    case 'medium':
      return 'secondary';
    case 'small':
      return 'outline';
    default:
      return 'outline';
  }
}

/**
 * Get badge variant based on customer volume
 */
export function getCustomerVolumeBadgeVariant(customerVolume: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (customerVolume) {
    case 'high':
      return 'default';
    case 'medium':
      return 'secondary';
    case 'low':
      return 'outline';
    default:
      return 'outline';
  }
}

/**
 * Get icon name based on outlet type
 */
export function getOutletTypeIcon(outletType: string): string {
  switch (outletType) {
    case 'grocery':
      return 'shopping-cart';
    case 'convenience':
      return 'store';
    case 'restaurant':
      return 'utensils';
    case 'minishop':
      return 'home';
    default:
      return 'store';
  }
}

/**
 * Get operating hours icon
 */
export function getOperatingHoursIcon(operatingHours: string): string {
  switch (operatingHours) {
    case '24_7':
      return 'clock';
    case 'day_only':
      return 'sun';
    default:
      return 'clock';
  }
}

/**
 * Convert English tier name to Vietnamese tier name
 * @param tier - English tier name (e.g., 'Gold', 'Silver', 'Bronze')
 * @returns Vietnamese tier name
 */
export function getTierNameInVietnamese(tier: string): string {
  const tierMap: Record<string, string> = {
    'Gold': 'Vàng',
    'Silver': 'Bạc', 
    'Bronze': 'Đồng',
    'Platinum': 'Bạch Kim',
    'Diamond': 'Kim Cương',
    'Ruby': 'Ruby',
    'Sapphire': 'Sapphire',
    'Emerald': 'Ngọc Lục Bảo',
    'Pearl': 'Ngọc Trai',
    'Crystal': 'Pha Lê',
    'Titanium': 'Titan',
    'Steel': 'Thép',
    'Iron': 'Sắt',
    'Copper': 'Đồng',
    'Tin': 'Thiếc',
    'Lead': 'Chì',
    'Zinc': 'Kẽm',
    'Aluminum': 'Nhôm',
    'Magnesium': 'Magie',
    'Calcium': 'Canxi',
    'Potassium': 'Kali',
    'Sodium': 'Natri',
    'Chlorine': 'Clo',
    'Fluorine': 'Flo',
    'Bromine': 'Brom',
    'Iodine': 'Iot',
    'Argon': 'Argon',
    'Neon': 'Neon',
    'Helium': 'Heli',
    'Hydrogen': 'Hidro',
    'Carbon': 'Cacbon',
    'Nitrogen': 'Nitơ',
    'Oxygen': 'Oxi',
    'Phosphorus': 'Photpho',
    'Sulfur': 'Lưu huỳnh',
    'Silicon': 'Silic',
    'Germanium': 'Gecmani',
    'Arsenic': 'Asen',
    'Selenium': 'Selen',
    'Tellurium': 'Telua',
    'Polonium': 'Poloni',
    'Astatine': 'Astat',
    'Radon': 'Radon',
    'Francium': 'Franxi',
    'Radium': 'Radi',
    'Actinium': 'Actini',
    'Thorium': 'Thori',
    'Protactinium': 'Protactini',
    'Uranium': 'Urani',
    'Neptunium': 'Neptuni',
    'Plutonium': 'Plutoni',
    'Americium': 'Americi',
    'Curium': 'Curi',
    'Berkelium': 'Berkeli',
    'Californium': 'Californi',
    'Einsteinium': 'Einsteini',
    'Fermium': 'Fermi',
    'Mendelevium': 'Mendelevi',
    'Nobelium': 'Nobeli',
    'Lawrencium': 'Lawrenci',
    'Rutherfordium': 'Rutherfordi',
    'Dubnium': 'Dubni',
    'Seaborgium': 'Seaborgi',
    'Bohrium': 'Bohri',
    'Hassium': 'Hassi',
    'Meitnerium': 'Meitneri',
    'Darmstadtium': 'Darmstadti',
    'Roentgenium': 'Roentgeni',
    'Copernicium': 'Copernici',
    'Nihonium': 'Nihoni',
    'Flerovium': 'Flerovi',
    'Moscovium': 'Moscovi',
    'Livermorium': 'Livermori',
    'Tennessine': 'Tennessi',
    'Oganesson': 'Oganesson'
  };

  // Return Vietnamese name if found, otherwise return original tier
  return tierMap[tier] || tier;
}
